import type { MeSession, PastSession } from "../hooks/sessionHooks";

export const CHECKIN_PROMPT_WINDOW_HOURS = 24;
export const FEEDBACK_PROMPT_WINDOW_HOURS = 48;

export type HomePrompt =
  | { kind: "invite" }
  | { kind: "feedback"; sessionUuid: string; sessionNumber: number; endedAtLocal: string }
  | { kind: "checkin"; sessionUuid: string; startsAtLocal: string }
  | { kind: "report"; reportUuid: string; title: string };

interface ReportPromptInput {
  uuid: string;
  title: string;
}

function hoursBetween(start: Date, end: Date): number {
  return (end.getTime() - start.getTime()) / (60 * 60 * 1000);
}

export function rankHomePrompts(input: {
  partnerJoined: boolean;
  lastEndedSession: PastSession | null;
  nextSession: MeSession | null;
  nextSessionCheckinSubmitted: boolean;
  // Server-authoritative save-until-start rule (§2.2, `checkin_window_open`).
  // Answers are final once saved, so a submitted check-in has no card at all.
  nextSessionCheckinWindowOpen: boolean;
  newestUnreadReport: ReportPromptInput | null;
  now: Date;
}): HomePrompt[] {
  const prompts: HomePrompt[] = [];

  if (!input.partnerJoined) {
    prompts.push({ kind: "invite" });
  }

  if (
    input.lastEndedSession &&
    !input.lastEndedSession.hasFeedback &&
    input.lastEndedSession.feedbackWindowOpen &&
    hoursBetween(new Date(input.lastEndedSession.endUtc), input.now) <= FEEDBACK_PROMPT_WINDOW_HOURS
  ) {
    prompts.push({
      kind: "feedback",
      sessionUuid: input.lastEndedSession.uuid,
      sessionNumber: input.lastEndedSession.sessionNumber,
      endedAtLocal: input.lastEndedSession.endLocal,
    });
  }

  if (input.nextSession && !input.nextSessionCheckinSubmitted && input.nextSessionCheckinWindowOpen) {
    const hoursUntilStart = hoursBetween(input.now, new Date(input.nextSession.startUtc));
    if (hoursUntilStart > 0 && hoursUntilStart <= CHECKIN_PROMPT_WINDOW_HOURS) {
      prompts.push({
        kind: "checkin",
        sessionUuid: input.nextSession.uuid,
        startsAtLocal: input.nextSession.startLocal,
      });
    }
  }

  if (input.newestUnreadReport) {
    prompts.push({
      kind: "report",
      reportUuid: input.newestUnreadReport.uuid,
      title: input.newestUnreadReport.title,
    });
  }

  return prompts;
}
